/*
 * tbb-creator-curation.js - creator-only curation tools on the storefront.
 *
 * WHAT THIS IS
 * A signed-in creator on a throwback's page gets a slim strip of their own
 * tools directly above the buy buttons: add the throwback to their Time
 * Capsule, claim its bounty when one is live, ask the seller for a bounty, or
 * remove it again. Spec + portal contract: ~/Code/TBB/docs/
 * CREATOR_CURATION_STRIP_SPEC.md (theme, 2026-09-24).
 *
 * THE RULE THAT OUTRANKS EVERYTHING ELSE
 * Buyers never see one byte of this. There is no server-rendered markup, no
 * hidden mount point, nothing reserved in the layout. This file creates the
 * strip only after the portal has verified, server-side, that the visitor is
 * an active creator. Anything short of that (no hint cookie, a timeout, a
 * 401, a malformed body) renders nothing, and the page stays the buyer page.
 *
 * COST TO BUYERS: ZERO REQUESTS
 * The portal sets a harmless, non-secret hint cookie (`tbb_creator=1`) next
 * to the real HttpOnly session cookie when a creator signs in. No hint means
 * this module returns before touching the network. The hint is never trusted
 * for rendering; a forged one only earns the forger a request that answers
 * `authenticated:false`.
 *
 * IDENTITY IS NON-BLOCKING AND NEVER CACHED
 * Every page view with the hint asks GET /api/creator/session, so the strip
 * always belongs to the account the portal session holds right now. The old
 * 10-minute sessionStorage identity cache let a just-switched account inherit
 * the previous account's strip (Portal ledger 2026-10-16); it is gone, and its
 * leftover key is wiped on load. Concurrent mounts on one page share a single
 * in-flight request. A strip already on screen is re-checked when the tab
 * comes back into view or the page is restored from the back/forward cache,
 * and is swapped (or removed) if the account changed. Per-throwback state is
 * never cached either: bounties get claimed and throwbacks sell under the
 * creator's feet, and the server is the only source of truth for every state
 * (spec section 3).
 *
 * ONE SHARED CONTROL
 * `TBBCreatorCurate.mount(host, { productId, variant })` renders the control
 * into any host. v1 mounts the "strip" variant on the PDP only. Cards and
 * carousels (v1.1) mount the "chip" variant through the same function; they
 * do not ship until the PDP is proven.
 *
 * EVERY ACTION HAS THREE VISIBLE PHASES
 * pending (aria-busy, disabled tones, "-ing" label), success (re-render from
 * the server's response + a status line), failure (controls re-enable + a
 * status line with the server's message). The Memory Wall submit bug (a
 * button that froze silently) is the reference failure; nothing here is
 * allowed to go quiet.
 *
 * Depends on TBBFetch (assets/tbb-fetch.js, loaded in <head>) for the
 * deadline; every portal call carries credentials so the session cookie
 * rides. (theme, 2026-09-24)
 */
(function () {
  'use strict';

  if (window.TBBCreatorCurate) return; // one instance per page, however many times the snippet renders

  var PORTAL = 'https://portal.throwbackbuys.com';
  var HINT_COOKIE = 'tbb_creator';
  var LEGACY_CACHE_KEY = 'tbb_creator_session'; // the retired identity cache; wiped on load, never read
  var TIMEOUT_MS = 3000;                // reads (identity, state): fail closed fast, the page never waits
  // Actions wait longer. The portal's add path writes the creator tag, the
  // capsule order and the reward pair before it answers, and on a phone the
  // preflight + POST alone cost 1-2s. At 3s the write LANDED but the theme
  // said "didn't go through", and the second tap hit the idempotent path
  // and "worked" (Desi, 2026-10-05, 3 of 3 throwbacks). The pending state
  // is visible the whole time, so waiting is honest; guessing was not.
  var ACTION_TIMEOUT_MS = 15000;
  var STATUS_HOLD_MS = 4000;            // success line lingers, then clears
  var ROOT_CLASS = 'tbb-creator-strip';

  // Copy: ratified by the TOV pass, ledger 2026-09-24 "[theme] copy" (spec
  // section 6). Creator-facing, so bounty language and live amounts are
  // allowed. Controls are mono uppercase and stay under 14 characters (18 is
  // the hard cap after the amount is interpolated). The eyebrow is uppercased
  // by CSS. Portal-owned sentences (lockedMessage, every error `message`) are
  // shown verbatim; only "To Do" inside a setup guard becomes a link.
  var COPY = {
    eyebrow: 'Curator tools',
    claim: function (amt) { return 'Claim ' + amt + ' Bounty \uD83C\uDFAF'; }, // LeMarc 2026-10-08: "Claim $X Bounty" + target emoji
    claimConfirm: function (amt) { return amt ? 'Claim this ' + amt + ' Bounty?' : 'Claim this Bounty?'; },
    claimLock: 'This Bounty stays claimed until the Throwback sells.',
    notNow: 'Not now',
    add: 'Add to Capsule',
    earn: function (rate) { return 'Earns ' + rate + '% when you drive the sale'; },
    request: 'Request Bounty',
    requested: 'Requested',
    inCapsule: 'In your Time Capsule',
    inCapsuleClaim: function (amt) { return 'In your Time Capsule with a ' + amt + ' bounty'; },
    remove: 'Remove',
    ineligible: {
      auction: 'Auctions can\'t be added to your Time Capsule.',
      sold_out: 'Sold-out throwbacks can\'t be added to your Time Capsule.',
      delisted: 'This throwback isn\'t available right now.',
      unavailable: 'This throwback isn\'t available right now.'
    },
    inactive: 'Finish setting up your Time Capsule in the portal to curate from here.',
    openPortal: 'Open Portal',
    pending: { add: 'Adding...', claim: 'Claiming...', request: 'Requesting...', remove: 'Removing...' },
    success: {
      add: 'Added to your Time Capsule',
      claim: 'Claimed. It\'s in your Time Capsule.',
      request: 'Request sent to the Nostalgia Seller.',
      remove: 'Removed from your Time Capsule'
    },
    failure: 'That didn\'t go through. Try again.',
    toDo: 'To Do'
  };

  /* ── helpers ──────────────────────────────────────────────────────── */

  function hasHint() {
    return new RegExp('(?:^|;\\s*)' + HINT_COOKIE + '=1(?:;|$)').test(document.cookie || '');
  }

  function dropLegacyCache() {
    try { sessionStorage.removeItem(LEGACY_CACHE_KEY); } catch (e) { /* private mode / blocked storage: nothing to drop */ }
  }

  // Same account and same standing? Anything else means the strip on screen is stale.
  function sameCreator(a, b) {
    return !!a && !!b && a.handle === b.handle && a.kind === b.kind && a.status === b.status;
  }

  function money(amount) {
    // Bounties are seller-set flat dollar figures (CREATOR_BOUNTY_SPEC section 1).
    // Whole dollars render as "$40"; anything else keeps two decimals.
    var n = Number(amount);
    if (!isFinite(n)) return '';
    return '$' + (Math.round(n) === n ? String(n) : n.toFixed(2));
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text; // textContent only: server strings are never HTML
    return n;
  }

  function api(path, opts) {
    opts = opts || {};
    var init = {
      method: opts.method || 'GET',
      credentials: 'include',
      timeout: opts.timeout || TIMEOUT_MS,
      headers: {}
    };
    if (opts.body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    var doFetch = window.TBBFetch || window.fetch; // TBBFetch is the deadline; plain fetch is the last resort
    return doFetch(PORTAL + path, init).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (json) {
        return { status: res.status, ok: res.ok, json: json };
      });
    });
  }

  /* ── identity ─────────────────────────────────────────────────────── */

  // Resolves to a creator object or null. Never throws. Never blocks the page.
  // Always asks the portal (no cache); calls that overlap share one request.
  var identityInFlight = null;
  function identity() {
    if (identityInFlight) return identityInFlight;
    var p = api('/api/creator/session').then(function (r) {
      return (r.ok && r.json && r.json.authenticated === true && r.json.creator && typeof r.json.creator.handle === 'string')
        ? r.json.creator : null;
    }).catch(function () { return null; }); // timeout / network: fail closed, nothing renders
    identityInFlight = p;
    p.then(function () { if (identityInFlight === p) identityInFlight = null; });
    return p;
  }

  /* ── rendering ────────────────────────────────────────────────────── */

  function Strip(host, opts, creator) {
    this.host = host;
    this.productId = opts.productId;
    this.variant = opts.variant || 'strip';
    this.creator = creator;
    this.root = el('div', ROOT_CLASS + ' ' + ROOT_CLASS + '--' + this.variant);
    this.root.setAttribute('data-tbb-creator-strip', '');
    this.who = this.buildWho();
    this.note = el('p', ROOT_CLASS + '__note');
    this.actions = el('div', ROOT_CLASS + '__actions');
    this.status = el('p', ROOT_CLASS + '__status');
    this.status.setAttribute('aria-live', 'polite');
    this.root.appendChild(this.who);
    this.root.appendChild(this.note);
    this.root.appendChild(this.actions);
    this.root.appendChild(this.status);
    this.statusTimer = null;
    this.state = null;
  }

  Strip.prototype.buildWho = function () {
    var c = this.creator;
    var who = el('div', ROOT_CLASS + '__who');
    var avatar = el('span', ROOT_CLASS + '__avatar');
    avatar.setAttribute('aria-hidden', 'true');
    if (c.avatarUrl) {
      var img = document.createElement('img');
      img.src = c.avatarUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 28; img.height = 28;
      avatar.appendChild(img);
    } else {
      // No avatar saved yet: first letter of the handle in mono, same fallback the seller header uses.
      avatar.appendChild(el('span', ROOT_CLASS + '__avatar-letter', (c.handle || '?').charAt(0).toUpperCase()));
    }
    var text = el('div', ROOT_CLASS + '__who-text');
    text.appendChild(el('span', ROOT_CLASS + '__handle', '@' + c.handle));
    text.appendChild(el('span', ROOT_CLASS + '__eyebrow', COPY.eyebrow));
    who.appendChild(avatar);
    who.appendChild(text);
    return who;
  };

  Strip.prototype.attach = function () {
    // The strip goes immediately BEFORE the host (the buy-buttons block), spec section 2.
    if (!this.root.parentNode) this.host.parentNode.insertBefore(this.root, this.host);
  };

  Strip.prototype.detach = function () {
    if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
    untrack(this);
  };

  Strip.prototype.button = function (label, kind, secondary) {
    var b = el('button', 'tbb-btn tbb-btn-condensed' + (secondary ? ' tbb-btn--secondary' : '') + ' ' + ROOT_CLASS + '__btn', label);
    b.type = 'button';
    b.setAttribute('data-action', kind);
    // The creator accent (pink) marks every control in this strip as "your tool", spec section 2.
    b.style.setProperty('--tbb-btn-accent', 'var(--tbb-accent-pink)');
    var self = this;
    b.addEventListener('click', function () {
      if (kind === 'claim') { self.confirmClaim(); return; }
      if (kind === 'cancel-claim') { self.render(self.state); return; }
      if (kind === 'confirm-claim') { self.act('claim', b); return; }
      self.act(kind, b);
    });
    return b;
  };

  // Claim changes attribution and locks the Throwback in the Creator's capsule
  // while its Seller Bounty is live. Keep that one mutation behind an explicit
  // second action; Add, Request and removable-state cleanup keep their existing
  // one-step behavior.
  Strip.prototype.confirmClaim = function () {
    var state = this.state;
    if (!state || !state.bounty || state.bounty.live !== true) return;
    var amt = money(state.bounty.amount);
    this.setStatus('');
    this.note.textContent = COPY.claimConfirm(amt);
    this.note.hidden = false;
    this.note.classList.remove(ROOT_CLASS + '__note--confirmed');
    this.note.appendChild(el('span', ROOT_CLASS + '__locked', COPY.claimLock));
    this.actions.textContent = '';
    this.actions.setAttribute('role', 'group');
    this.actions.setAttribute('aria-label', 'Confirm Bounty claim');
    var cancel = this.button(COPY.notNow, 'cancel-claim', true);
    this.actions.appendChild(cancel);
    this.actions.appendChild(this.button(COPY.claim(amt), 'confirm-claim', false));
    this.actions.hidden = false;
    cancel.focus();
  };

  // Render exactly what the server said (spec section 3). No inference.
  Strip.prototype.render = function (state) {
    this.state = state;
    // Claim-only hybrids (Sellers enrolled in Creator Earnings, Portal contract
    // 2026-10-08): they only ever see the strip where they can Claim, or on a
    // throwback they already claimed. Everywhere else it stays out of the way.
    var hybrid = !!(this.creator && this.creator.kind === 'hybrid');
    this.root.style.display = '';
    this.note.textContent = '';
    this.note.hidden = true;
    this.note.classList.remove(ROOT_CLASS + '__note--confirmed');
    this.actions.textContent = '';
    this.actions.removeAttribute('role');
    this.actions.removeAttribute('aria-label');
    this.actions.hidden = true;

    var live = !!(state && state.bounty && state.bounty.live === true);
    var amt = live ? money(state.bounty.amount) : '';
    // A claimed bounty keeps its amount even once it is no longer "live".
    var claimedAmt = state && state.bounty && state.bounty.amount != null ? money(state.bounty.amount) : '';

    // E: in the capsule. Existing membership outranks generic ineligibility
    // (Portal ledger 2026-10-16): an ended Claim, or a Pick that sold out, is
    // still in the capsule, so its confirmed state and its Remove (or the
    // portal's lockedMessage) always show, for hybrids too.
    if (state && (state.membership === 'pick' || state.membership === 'claim')) {
      this.note.textContent = state.membership === 'claim' && claimedAmt ? COPY.inCapsuleClaim(claimedAmt) : COPY.inCapsule;
      this.note.hidden = false;
      this.note.classList.add(ROOT_CLASS + '__note--confirmed');
      // E2 (Portal contract 2026-10-08): a percentage Pick whose throwback gained a live
      // Bounty stays a Pick until the Creator explicitly claims it. Claim is the
      // primary action, Remove stays secondary. Same POST as Add; the portal
      // converts the saved intent to claim (409 setup guard shown verbatim).
      // Only offered while the portal still calls the throwback eligible.
      if (state.membership === 'pick' && live && state.eligible !== false) {
        this.actions.appendChild(this.button(COPY.claim(amt), 'claim', false));
        this.actions.hidden = false;
      }
      if (state.removable === true) {
        this.actions.appendChild(this.button(COPY.remove, 'remove', true));
        this.actions.hidden = false;
      } else if (state.lockedMessage) {
        this.note.appendChild(el('span', ROOT_CLASS + '__locked', ' ' + state.lockedMessage));
      }
      return;
    }

    if (!state || state.eligible === false) {                     // A: ineligible, controls absent
      if (hybrid) { this.root.style.display = 'none'; return; }   // no_bounty, own_listing, auction, sold out...
      var reason = state && state.ineligibleReason;
      this.note.textContent = COPY.ineligible[reason] || COPY.ineligible.unavailable;
      this.note.hidden = false;
      return;
    }

    if (live) {                                                    // B: claim the live bounty
      this.actions.appendChild(this.button(COPY.claim(amt), 'claim', false));
    } else if (hybrid) {                                           // hybrids never Pick or Request
      this.root.style.display = 'none';
      return;
    } else {                                                       // C / D: add, plus request or its locked chip
      this.actions.appendChild(this.button(COPY.add, 'add', false));
      var rate = Number(this.creator && this.creator.standardEarningsRatePercent);
      this.actions.appendChild(el('p', ROOT_CLASS + '__hint', COPY.earn(isFinite(rate) && rate > 0 ? rate : 3))); // sits right under Add on phones, own line under the row from 768px
      // Request Bounty only when the portal says so (bountyRequestEligible,
      // Portal 76b801f: price floor, requests enabled, Seller accepts them).
      // Never reconstructed here. An existing request keeps its chip.
      if (state.bountyRequested === true) {
        this.actions.appendChild(el('span', ROOT_CLASS + '__chip', COPY.requested));
      } else if (state.bountyRequestEligible === true) {
        this.actions.appendChild(this.button(COPY.request, 'request', true));
      }
    }
    this.actions.hidden = false;
  };

  Strip.prototype.renderInactive = function () {                   // F: creator, not yet activated
    this.note.textContent = COPY.inactive;
    this.note.hidden = false;
    this.actions.textContent = '';
    var a = el('a', 'tbb-btn tbb-btn-condensed tbb-btn--secondary ' + ROOT_CLASS + '__btn', COPY.openPortal);
    a.href = this.creator.portalUrl || PORTAL;
    a.style.setProperty('--tbb-btn-accent', 'var(--tbb-accent-pink)');
    this.actions.appendChild(a);
    this.actions.hidden = false;
  };

  Strip.prototype.setStatus = function (text, isError) {
    var self = this;
    clearTimeout(this.statusTimer);
    this.status.textContent = '';
    text = text || '';
    // Setup guards name the creator's To Do list; only that phrase becomes a
    // link, to the portal destination the identity call gave us. Everything
    // else stays plain text (server strings are never HTML).
    var i = text.indexOf(COPY.toDo);
    if (i > -1 && this.creator && this.creator.portalUrl) {
      this.status.appendChild(document.createTextNode(text.slice(0, i)));
      var a = el('a', ROOT_CLASS + '__link', COPY.toDo);
      a.href = this.creator.portalUrl;
      this.status.appendChild(a);
      this.status.appendChild(document.createTextNode(text.slice(i + COPY.toDo.length)));
    } else {
      this.status.textContent = text;
    }
    this.status.classList.toggle(ROOT_CLASS + '__status--error', !!isError);
    if (text && !isError) this.statusTimer = setTimeout(function () { self.status.textContent = ''; }, STATUS_HOLD_MS);
  };

  Strip.prototype.setBusy = function (btn, kind, busy) {
    var buttons = this.actions.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) buttons[i].disabled = busy; // siblings freeze too; no double submits
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('data-label', btn.textContent);
      btn.textContent = COPY.pending[kind] || btn.textContent;
    } else {
      btn.removeAttribute('aria-busy');
      if (btn.getAttribute('data-label')) btn.textContent = btn.getAttribute('data-label');
      btn.removeAttribute('data-label');
    }
  };

  Strip.prototype.act = function (kind, btn) {
    if (btn.disabled) return;
    var self = this;
    var req;
    if (kind === 'add' || kind === 'claim') {
      req = api('/api/creator/capsule/items', { method: 'POST', body: { productId: this.productId }, timeout: ACTION_TIMEOUT_MS });
    } else if (kind === 'remove') {
      req = api('/api/creator/capsule/items/' + encodeURIComponent(this.productId), { method: 'DELETE', timeout: ACTION_TIMEOUT_MS });
    } else if (kind === 'request') {
      req = api('/api/creator/bounty-requests', { method: 'POST', body: { productId: this.productId }, timeout: ACTION_TIMEOUT_MS });
    } else {
      return;
    }
    this.setStatus('');
    this.setBusy(btn, kind, true);
    req.then(function (r) {
      if (r.status === 401) { self.detach(); return; } // signed out elsewhere: back to the buyer page
      var hasState = r.json && typeof r.json === 'object' && 'eligible' in r.json;
      if (hasState) self.render(r.json); // the response IS the new state, success or 409
      else if (r.ok) return self.refresh().then(function () { self.setStatus(COPY.success[kind] || '', false); });
      else self.setBusy(btn, kind, false);
      if (r.ok) {
        self.setStatus(COPY.success[kind] || '', false);
      } else {
        // A creator-safe sentence from the portal is shown on its own; the
        // network fallback only appears when the portal said nothing.
        var msg = (r.json && typeof r.json.message === 'string') ? r.json.message.trim() : '';
        self.setStatus(msg || COPY.failure, true);
      }
    }).catch(function () {
      // The answer got lost (deadline, network). The write may still have
      // landed, so ask the portal what is true before saying anything.
      self.reload().then(function (state) {
        if (state && landed(kind, state)) {
          self.render(state);
          self.setStatus(COPY.success[kind] || '', false);
        } else {
          if (state) self.render(state); else self.setBusy(btn, kind, false);
          self.setStatus(COPY.failure, true);
        }
      });
    });
  };

  // Did the action's intended outcome show up in a fresh state read?
  function landed(kind, state) {
    // A claim only landed if the portal now says claim: a Pick-to-Claim tap starts
    // from membership "pick", so "pick" must not count as success here.
    if (kind === 'claim') return state.membership === 'claim';
    if (kind === 'add') return state.membership === 'pick' || state.membership === 'claim';
    if (kind === 'remove') return state.membership === 'none';
    if (kind === 'request') return state.bountyRequested === true;
    return false;
  }

  // Fresh state from the portal, or null. Never throws.
  Strip.prototype.reload = function () {
    var self = this;
    return api('/api/creator/capsule/state?productId=' + encodeURIComponent(this.productId)).then(function (r) {
      if (r.status === 401) { self.detach(); return null; }
      return (r.ok && r.json && typeof r.json === 'object' && 'eligible' in r.json) ? r.json : null;
    }).catch(function () { return null; });
  };

  // Re-read the state from the server (used when an action answered without one).
  Strip.prototype.refresh = function () {
    var self = this;
    return api('/api/creator/capsule/state?productId=' + encodeURIComponent(this.productId)).then(function (r) {
      if (r.status === 401) { self.detach(); return; }
      if (r.ok && r.json && typeof r.json === 'object' && 'eligible' in r.json) self.render(r.json);
      else self.setStatus(COPY.failure, true);
    }).catch(function () { self.setStatus(COPY.failure, true); });
  };

  /* ── mount ────────────────────────────────────────────────────────── */

  // Strips on screen right now, so an account switch can be caught without a reload.
  var mounted = [];
  function track(strip, host, opts) { mounted.push({ strip: strip, host: host, opts: opts }); }
  function untrack(strip) {
    for (var i = mounted.length - 1; i >= 0; i--) if (mounted[i].strip === strip) mounted.splice(i, 1);
  }

  // host: the element the control renders BEFORE (strip) or INTO (chip, v1.1).
  // Resolves after the first render, or immediately (undefined) when nothing
  // should show. Safe to call for anyone: buyers cost nothing.
  function mount(host, opts) {
    if (!host || !opts || !opts.productId) return Promise.resolve();
    if (!hasHint()) return Promise.resolve();
    return identity().then(function (creator) { return mountAs(host, opts, creator); });
  }

  // Render for an identity the portal just verified (never a remembered one).
  function mountAs(host, opts, creator) {
    if (!creator) return Promise.resolve();
    var strip = new Strip(host, opts, creator);
    if (creator.status === 'inactive') {
      strip.renderInactive();
      strip.attach();
      track(strip, host, opts);
      return Promise.resolve(strip);
    }
    return api('/api/creator/capsule/state?productId=' + encodeURIComponent(opts.productId)).then(function (r) {
      if (r.status === 401) return;
      if (!r.ok || !r.json || typeof r.json !== 'object' || !('eligible' in r.json)) return; // fail closed
      strip.render(r.json);
      strip.attach();
      track(strip, host, opts);
      return strip;
    }).catch(function () { /* timeout / network: render nothing */ });
  }

  // A strip on screen belongs to whoever was signed in when it rendered. When
  // the tab comes back (the Creator may have switched account or workspace in
  // the portal) or the page returns from the back/forward cache, ask again:
  // same account keeps its strip; another account gets its own strip; signed
  // out (hint gone, or the portal says no) gets the buyer page. Only runs when
  // a strip exists, so buyers still cost nothing.
  function revalidate() {
    if (!mounted.length) return Promise.resolve();
    var entries = mounted.slice();
    if (!hasHint()) { entries.forEach(function (e) { e.strip.detach(); }); return Promise.resolve(); }
    return identity().then(function (creator) {
      return Promise.all(entries.map(function (e) {
        if (sameCreator(e.strip.creator, creator)) return null;
        e.strip.detach();
        return creator ? mountAs(e.host, e.opts, creator) : null;
      }));
    });
  }

  /* ── PDP auto-mount (v1) ──────────────────────────────────────────── */

  function pdpProductId() {
    // Shopify emits `meta.product.id` on every product page; nothing of ours is added to the HTML.
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (m && m.id) return String(m.id);
    } catch (e) { /* fall through */ }
    return null;
  }

  function pdpAnchor() {
    // The product-info block that holds the BUY form. Keyed on the form, not on
    // block order, so it survives theme-editor rearrangement. Two forms post to
    // /cart/add on a Diffusion PDP: the price block's installment form (hidden
    // input + payment terms only) and the real one with the submit button, and
    // the installment form comes first in the DOM (found the hard way,
    // 2026-09-24). Only the form with a submit button counts; sold-out
    // throwbacks still render that button, so the "no controls" strip lands.
    // Quick-view modals are skipped: the strip belongs on the page, not in a popup.
    var blocks = document.querySelectorAll('#MainContent [data-meta-block]');
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i].closest('[id^="ProductQuickView"]')) continue;
      if (blocks[i].querySelector('form[action*="/cart/add"] button[type="submit"]')) return blocks[i];
    }
    return null;
  }

  function autoMountPdp() {
    if (!/\/products\//.test(location.pathname)) return;
    if (!hasHint()) return; // buyers: not one more line runs
    var id = pdpProductId();
    var anchor = pdpAnchor();
    if (!id || !anchor) return;
    mount(anchor, { productId: id, variant: 'strip' });
  }

  window.TBBCreatorCurate = { mount: mount, revalidate: revalidate };

  dropLegacyCache();
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') revalidate();
  });
  window.addEventListener('pageshow', function (e) {
    if (!e || !e.persisted) return;            // first load is handled below
    if (mounted.length) revalidate(); else autoMountPdp();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMountPdp);
  else autoMountPdp();
})();
